import {
    ClockCircleOutlined,
    EnvironmentOutlined,
    UserOutlined,
    UserSwitchOutlined,
} from "@ant-design/icons";
import { Card, Flex, Tag } from "antd";
import dayjs from "dayjs";
import { FC, MouseEvent, useCallback } from "react";
import { AnyTask, Coords, TaskRoute } from "tm-shared/types";

import { StyledCoordsLink } from "@/components/TasksModals/components/TaskCard/Styled";
import { PrimaryText } from "@/components/Typography/Styled";

import { MetaRow } from "./MetaRow";

interface TaskCardProps {
    task: AnyTask;
    onOpenModal: () => void;
}

const formatDateTime = (value?: string) => {
    if (!value) return "—";
    const d = dayjs(value);
    return d.isValid() ? d.format("DD MMMM YYYY HH:mm") : value;
};

const formatCoords = (geo?: Coords) => {
    if (!geo) return "—";
    return `${geo.lat.toFixed(6)}, ${geo.lng.toFixed(6)}`;
};

export const TaskCard: FC<TaskCardProps> = ({ task, onOpenModal }) => {
    const hasRealPerformer =
        task?.info?.realPerformer?.userId !== task?.info?.performer?.userId;
    const isDone = task.completed === true;

    const performerName = task.info?.realPerformer?.name ?? "—";
    const completedAt = formatDateTime(task.info?.context?.completedAt);
    const coords = task.info?.context?.geo;

    const formattedCoords = formatCoords(coords);

    const handleClickCoords = useCallback(
        (evt: MouseEvent<HTMLAnchorElement>) => {
            evt.stopPropagation();
            if (!coords) evt.preventDefault();
        },
        [coords],
    );

    const handleCardClick = useCallback(() => {
        if (!isDone || task.context.route === TaskRoute.StartVisit) return;

        onOpenModal();
    }, [isDone, onOpenModal, task.context.route]);

    return (
        <Card
            size="small"
            title={
                <Flex gap={8} justify="space-between" align="flex-start">
                    <PrimaryText
                        style={{
                            whiteSpace: "normal",
                        }}
                    >
                        {task.name}
                    </PrimaryText>

                    <Tag color={isDone ? "green" : "red"} style={{ margin: 0 }}>
                        {isDone ? "Завершено" : "Не завершено"}
                    </Tag>
                </Flex>
            }
            styles={{
                header: { padding: "8px 12px" },
                body: { padding: isDone ? 12 : 0 },
            }}
            onClick={handleCardClick}
        >
            {isDone && (
                <Flex vertical gap={8}>
                    <MetaRow icon={<UserOutlined />} label="Виконавець">
                        <PrimaryText ellipsis>{performerName}</PrimaryText>
                    </MetaRow>
                    {hasRealPerformer && (
                        <MetaRow
                            icon={<UserSwitchOutlined />}
                            label="Від імені"
                        >
                            <PrimaryText ellipsis>
                                {task.info?.performer?.name}
                            </PrimaryText>
                        </MetaRow>
                    )}

                    <MetaRow icon={<ClockCircleOutlined />} label="Дата та час">
                        <PrimaryText>{completedAt}</PrimaryText>
                    </MetaRow>

                    <MetaRow icon={<EnvironmentOutlined />} label="Геолокація">
                        <StyledCoordsLink
                            href={
                                coords
                                    ? `https://www.google.com/maps?q=${coords.lat},${coords.lng}`
                                    : undefined
                            }
                            target="_blank"
                            rel="noreferrer"
                            onClick={handleClickCoords}
                        >
                            <PrimaryText ellipsis $textColor={"accent"}>
                                {formattedCoords}
                            </PrimaryText>
                        </StyledCoordsLink>
                    </MetaRow>
                </Flex>
            )}
        </Card>
    );
};
